import React, { useState, useEffect } from "react";
import { CalendarDays, X } from "lucide-react";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";

const PastLeaves = () => {
  const [showModal, setShowModal] = useState(false);
  const [allHolidays, setAllHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear().toString();
  const { user } = useAuth();

  useEffect(() => {
    const fetchHolidays = async () => {
      const apiUrl = `${Base_URL}/HolidayMasterOrg/GetHolidaysByOrg/${user?.organisationId}`;

      try {
        setLoading(true);
        const response = await fetch(apiUrl);
        const data = await response.json();

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const transformedHolidays = data
          .filter((item) => item.yearId === currentYear)
          .map((item) => {
            const holidayDate = new Date(item.holidayDate);
            const dayName = holidayDate.toLocaleDateString("en-US", {
              weekday: "long",
            });
            const formattedDate = holidayDate.toLocaleDateString("en-GB", {
              day: "numeric",
              month: "short",
            });

            let status;
            if (holidayDate < today) {
              status = "past";
            } else if (holidayDate.toDateString() === today.toDateString()) {
              status = "today";
            } else {
              status = "upcoming";
            }

            return {
              name: item.holidayName,
              date: formattedDate,
              day: dayName,
              type: item.holidayType,
              status: status,
              sortDate: holidayDate,
            };
          })
          .sort((a, b) => a.sortDate - b.sortDate); // Sort by date

        setAllHolidays(transformedHolidays);
      } catch (error) {
        console.error("❌ Error fetching holidays:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHolidays();
  }, []);

  // Get upcoming holidays (next 30 days)
  const getUpcomingHolidays = () => {
    const today = new Date();
    const next30Days = new Date();
    next30Days.setDate(today.getDate() + 30);

    return allHolidays.filter((holiday) => {
      const holidayDate = holiday.sortDate;
      return holidayDate >= today && holidayDate <= next30Days;
    });
  };

  const upcomingHolidays = getUpcomingHolidays();

  const getStatusColor = (status) => {
    switch (status) {
      case "past":
        return "#94a3b8";
      case "today":
        return "#3b82f6";
      case "upcoming":
        return "#10b981";
      default:
        return "#64748b";
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case "National":
        return { bg: "#dbeafe", text: "#1e40af" };
      case "Festival":
        return { bg: "#fef3c7", text: "#92400e" };
      case "Optional":
        return { bg: "#f3e8ff", text: "#6b21a8" };
      default:
        return { bg: "#f3f4f6", text: "#374151" };
    }
  };

  

  return (
    <>
      <div
        className="pastleaves-card compact"
      >
        {/* Header */}
        <div className="pastleaves-header compact">
          <div className="pastleaves-icon small">
            <CalendarDays size={16} />
          </div>
          <h4>Upcoming Holidays</h4>
            <span
            className="header-badge clickable"
            onClick={(e) => {
              e.stopPropagation();
              setShowModal(true);
            }}
            >
            View All Holidays
            </span>
      </div>

        {/* Holidays List */}
        <div className="pastleaves-list compact">
        {loading ? (
            <div className="pastleaves-loading">
              Loading holidays...
            </div>): upcomingHolidays.length > 0 ? (
            upcomingHolidays.map((holiday, idx) => {
              const colors = getTypeColor(holiday.type);
              return (
                <div key={idx} className="pastleaves-row">
                  <span
                    className="leave-type-chip"
                    style={{
                      background: colors.bg,
                      color: colors.text,
                    }}
                  >
                    {holiday.type}
                  </span>
                  <span className="leave-dates">
                    {holiday.name} • {holiday.day}
                  </span>
                  <span className="leave-days">{holiday.date}</span>
                  <span className="leave-status upcoming">Upcoming</span>
                </div>
              );
            })
          ) : (
            <div className="pastleaves-empty">
              No upcoming holidays in next 30 days
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "500px",
              maxHeight: "85vh",
              overflow: "hidden",
              boxShadow:
                "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%)",
                color: "white",
                padding: "15px",
                border: "none",
              }}
            >
              <div>
                <h2
                  style={{
                    margin: 0,
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#fff",
                  }}
                >
                  All Holidays
                </h2>
                <p
                  style={{
                    margin: "4px 0 0 0",
                    fontSize: "14px",
                    color: "#e0e7ff",
                  }}
                >
                  {allHolidays.length} holidays in {currentYear}
                </p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: "rgba(255, 255, 255, 0.2)",
                  border: "none",
                  cursor: "pointer",
                  padding: "8px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "rgba(255, 255, 255, 0.3)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor =
                    "rgba(255, 255, 255, 0.2)";
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div
              style={{
                padding: "0",
                maxHeight: "calc(50vh - 80px)",
                overflowY: "auto",
              }}
            >
              <div style={{ padding: "14px 20px" }}>
                {allHolidays.map((holiday, idx) => {
                  const colors = getTypeColor(holiday.type);
                  return (
                    <div
                      key={idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "16px",
                        borderRadius: "8px",
                        backgroundColor: idx % 2 === 0 ? "#f9fafb" : "white",
                        marginBottom: "8px",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f3f4f6";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                          idx % 2 === 0 ? "#f9fafb" : "white";
                      }}
                    >
                      {/* Left side - Type badge */}
                      <div style={{ flex: "0 0 100px" }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "4px 12px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "500",
                            backgroundColor: colors.bg,
                            color: colors.text,
                          }}
                        >
                          {holiday.type}
                        </span>
                      </div>

                      {/* Middle - Name and day */}
                      <div style={{ flex: "1", paddingLeft: "16px" }}>
                        <div
                          style={{
                            fontSize: "15px",
                            fontWeight: "500",
                            color: "#111827",
                            marginBottom: "2px",
                          }}
                        >
                          {holiday.name}
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#6b7280",
                          }}
                        >
                          {holiday.day}
                        </div>
                      </div>

                      {/* Right - Date and status */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "16px",
                          flex: "0 0 200px",
                          justifyContent: "flex-end",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#374151",
                          }}
                        >
                          {holiday.date}
                        </div>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "4px 12px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "500",
                            backgroundColor: `${getStatusColor(holiday.status)}20`,
                            color: getStatusColor(holiday.status),
                            textTransform: "capitalize",
                            minWidth: "80px",
                            textAlign: "center",
                          }}
                        >
                          {holiday.status}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .pastleaves-card {
          background: white;
          border-radius: 8px;
          padding: 16px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .pastleaves-header {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
        }

        .pastleaves-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          color: #3b82f6;
        }

        .pastleaves-header h4 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #111827;
          flex: 1;
        }

        .header-badge {
          padding: 4px 8px;
          background: #dbeafe;
          color: #1e40af;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
        }

        .pastleaves-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .pastleaves-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: #f9fafb;
          border-radius: 6px;
        }

        .leave-type-chip {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          flex-shrink: 0;
        }
        .pastleaves-loading {
          text-align: center;
          font-size: 12px;
          color: #9ca3af;
          padding: 12px 0;
        }
        .leave-dates {
          flex: 1;
          font-size: 13px;
          color: #374151;
        }

        .leave-days {
          font-size: 13px;
          font-weight: 500;
          color: #111827;
        }

        .leave-status {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
        }

        .leave-status.upcoming {
          background: #d1fae5;
          color: #065f46;
        }

        .pastleaves-empty {
          padding: 24px;
          text-align: center;
          color: #6b7280;
          font-size: 14px;
        }
      `}</style>
    </>
  );
};

export default PastLeaves;
